import React from "react"
import { useTranslation, Trans } from "react-i18next"
import Image from "next/image"
import { Badge, Button, Typography, message } from "antd"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { capitalize } from "lodash"
import { EnrichedPlan, PlanNames, Price, activePlanFeatures } from "@/types/billingPlans"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import { useRecoilValue } from "recoil"
import {
  brandingColorsSelector,
  isCardDefaultPaymentMethodAtom,
  isOwnerAtom,
  userAtom,
} from "@/atoms/userAtom"
import { CountryCurrencies } from "@/constants/currencies"
import { createCheckoutSession } from "@/api/payments"
import { Currency } from "@/types/currencies.types"
import { useSandboxRouter } from "@/hooks/useSandboxRouter"
import { MAP_PAGE_PATH } from "@/constants/routes"
import { useStripeBillingPortal } from "@/hooks/useStripeBillingPortal"

interface PlanCardProps {
  plan: EnrichedPlan
  activePrice: Price
  isActive: boolean
  cancelAtPeriodEnd: boolean
  isUpgrade: boolean
  stripeUrl?: string
  currency: Currency
}

const { Title } = Typography

const PlanCard: React.FC<PlanCardProps> = ({
  isActive,
  cancelAtPeriodEnd,
  plan,
  isUpgrade,
  activePrice,
  currency,
}) => {
  const { t, i18n } = useTranslation()
  const { primaryColor } = useRecoilValue(brandingColorsSelector)
  const { language: currentLanguageI18n } = i18n
  const { sandboxRouter } = useSandboxRouter()

  const { org } = useRecoilValue(userAtom)
  const isOwner = useRecoilValue(isOwnerAtom)
  const isCardDefaultPayment = useRecoilValue(isCardDefaultPaymentMethodAtom)

  const { redirectToBillingPortal } = useStripeBillingPortal(isOwner)

  const addonText = plan.additionalOrderPrice
    ? t("plans.addon.additionalOrderPrice", {
        currency: currency?.symbol,
        orderPrice: (plan.additionalOrderPrice / 100).toFixed(2),
      })
    : ""

  const initCheckoutSession = async () => {
    try {
      const { data } = await createCheckoutSession(plan?.price?.id)
      window.open(data?.checkoutUrl, "_blank")
    } catch (error) {
      console.log(error)
      message.error("An error occured - please contact our team")
    }
  }

  const isFreePlan = plan.key === PlanNames.FREE

  const PlanCardComponent = (
    <div
      id="plan-card"
      className={`flex flex-col h-full space-y-4 justify-between border rounded-lg py-7 px-6 shadow-md `}
      style={{
        ...(isActive
          ? {
              boxShadow: "rgba(65, 0, 255, 0.2) 0px 2px 20px 0px",
              outline: "1px solid rgba(65, 0, 255, 0.4)",
              outlineColor: primaryColor,
            }
          : {}),
      }}
    >
      <div className="flex flex-col space-y-4">
        <div>
          <Image src={plan.icon} width={40} height={40} />
        </div>
        <div>
          <Title level={3}>{capitalize(plan.name)}</Title>
        </div>
        <div
          className={`${window.innerWidth < 768 ? "h-20" : "h-10"}`}
          style={{
            marginTop: "0",
          }}
        >
          <p className="text-base">
            {t(`plans.${plan.key.toLowerCase() || "launch"}.description`)}
          </p>
        </div>
        <div className="flex items-baseline space-x-1 h-10">
          <span className="text-xl font-bold">
            {activePrice?.amount === 0 && plan.id === "custom" ? (
              <span className="text-xl font-bold">
                {t("profile.EnterpriseSection.customPrice")}
              </span>
            ) : (
              <>
                <div className="flex">
                  <DisplayPrice
                    amount={activePrice?.amount}
                    currencyCode={currency?.code?.toUpperCase() as CountryCurrencies}
                    isCents
                    justify="start"
                    withDecimals={false}
                  />{" "}
                  <span className="text-lg font-normal">{t("profile.PlanCard.perMonth")}</span>
                </div>
              </>
            )}
          </span>
        </div>
        <div className="h-10">
          <p>
            <Trans>
              {!!plan.orderQuotes &&
                t("profile.PlanCard.upToOrdersMonthly", { maxOrders: plan.orderQuotes })}
            </Trans>
          </p>
        </div>
        <ul className="flex flex-col space-y-2">
          {activePlanFeatures?.[plan?.key]?.map((feature, index) => {
            if ((plan as any)[feature as any]) {
              return (
                <li key={index} className="flex space-x-2 items-center">
                  <div style={{ width: 20, height: 20, minWidth: 20, minHeight: 20 }}>
                    <Image
                      src="/assets/images/plansSettings/listIcon.svg"
                      width={"100%"}
                      height={"100%"}
                    />
                  </div>
                  <p>{t(`plans.features.${feature}`)}</p>
                </li>
              )
            } else {
              return <></>
            }
          })}
          {!!addonText && (
            <li key={addonText} className="flex space-x-2 items-center">
              <div style={{ width: 20, height: 20, minWidth: 20, minHeight: 20 }}>
                <Image
                  src="/assets/images/plansSettings/listIcon.svg"
                  width={"100%"}
                  height={"100%"}
                />
              </div>
              <p className="text-base">{addonText}</p>
            </li>
          )}
        </ul>
      </div>
      {isCardDefaultPayment && isOwner && (
        <div>
          {plan.id === "custom" ? (
            <Button
              type="primary"
              className="!flex !items-center !justify-center kosmo-btn-base pointer-events-auto w-full"
              size="large"
              onClick={() => {
                const ln =
                  currentLanguageI18n === "en" || currentLanguageI18n === "es"
                    ? currentLanguageI18n
                    : "en"
                // redirect to contact sales page in new tab
                window.open(`https://www.kosmo.delivery/${ln}/start`, "_blank")
              }}
            >
              {t("common.contactSales")}
            </Button>
          ) : (
            <>
              {!org.hasActiveSubscription || !org.hasStripeAccount ? (
                <KosmoButton
                  type="primary"
                  size="large"
                  classname="w-full"
                  onClick={
                    isFreePlan
                      ? () => sandboxRouter.pushWithContext({ targetHref: MAP_PAGE_PATH })
                      : initCheckoutSession
                  }
                >
                  {org?.hasValidPaymentMethod
                    ? t("profile.PlanCard.subscribe")
                    : isFreePlan
                    ? t("profile.planCard.startNow")
                    : t("profile.PlanCard.startYourFreeTrial")}
                </KosmoButton>
              ) : (
                <>
                  {org.hasStripeAccount && isOwner && (
                    <KosmoButton
                      id="manage-plan-button"
                      size="large"
                      type={isActive ? "default" : "primary"}
                      classname="w-full"
                      onClick={isActive ? redirectToBillingPortal : initCheckoutSession}
                    >
                      {isActive ? (cancelAtPeriodEnd ? t("common.renew") : t("common.cancel")) : ""}
                      {!isActive ? (isUpgrade ? t("common.upgrade") : t("common.downgrade")) : ""}
                    </KosmoButton>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )

  if (plan?.key === PlanNames.GROW) {
    return (
      <Badge.Ribbon text={t("common.recommended")} style={{ background: primaryColor }}>
        {PlanCardComponent}
      </Badge.Ribbon>
    )
  } else {
    return PlanCardComponent
  }
}

export default PlanCard
